// services/ChatContextManager.js - Version avec liens corrigés
import { askGPT, getAudioFromGPT } from '../components/OpenAIService';
import { CHAT_CONFIG } from './useAladinnData';

export class ChatContextManager {
  constructor() {
    this.lastMessageId = 0;
    this.audioCache = new Map();
  }

  // Construction du contexte des sources
  buildSourcesContext(savedSources) {
    if (!savedSources || savedSources.length === 0) return '';
    
    const sourcesContext = savedSources.map((source, index) => 
      `${index + 1}. ${source.title} (${source.domain}) - ${source.description.slice(0, 100)}...`
    ).join('\n');
    
    return `SOURCES DISPONIBLES POUR L'UTILISATEUR :\n${sourcesContext}\n`;
  }

  // Construction du contexte du plan d'action
  buildActionPlanContext(actionPlan) {
    if (!actionPlan || !actionPlan.steps || actionPlan.steps.length === 0) {
      return '';
    }

    const totalSteps = actionPlan.steps.length;
    const completedSteps = actionPlan.steps.filter(step => step.termine).length;
    const pendingSteps = actionPlan.steps.filter(step => !step.termine);
    const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

    let planContext = `PLAN D'ACTION DE L'UTILISATEUR :\n`;
    planContext += `Titre : ${actionPlan.titre}\n`;
    
    if (actionPlan.description) {
      planContext += `Description : ${actionPlan.description}\n`;
    }
    
    planContext += `Progression : ${completedSteps}/${totalSteps} étapes terminées (${progressPercentage}%)\n\n`;

    // Étapes terminées (max 3 dernières)
    if (completedSteps > 0) {
      planContext += `ÉTAPES TERMINÉES :\n`;
      const recentCompleted = actionPlan.steps
        .filter(step => step.termine)
        .slice(-3);
      
      recentCompleted.forEach((step, index) => {
        planContext += `✅ ${step.titre} - ${step.description.slice(0, 80)}...\n`;
      });
      planContext += '\n';
    }

    // Étapes en cours/à venir (max 5 prochaines)
    if (pendingSteps.length > 0) {
      planContext += `PROCHAINES ÉTAPES À RÉALISER :\n`;
      pendingSteps.slice(0, 5).forEach((step, index) => {
        planContext += `${index + 1}. ${step.titre}`;
        if (step.duree) {
          planContext += ` (Durée estimée: ${step.duree})`;
        }
        planContext += `\n   Description: ${step.description.slice(0, 120)}...\n`;
      });
    }

    return planContext;
  }

  // 🔧 CORRECTION PRINCIPALE : Construction du prompt système avec instructions pour le format des liens
  buildSystemPrompt({ userObjective, searchObjective, savedSources, conversationContext, actionPlan }) {
    const sourcesContext = this.buildSourcesContext(savedSources);
    const actionPlanContext = this.buildActionPlanContext(actionPlan);
    
    const basePrompt = `Tu es Aladinn, un assistant personnel, professionnel et coach virtuel spécialisé en développement personnel et professionnel.

CONTEXTE UTILISATEUR :
📋 Objectif Principal : "${userObjective || 'Non défini'}"
🎯 Objectif de Recherche : "${searchObjective || 'Non précisé'}"

${sourcesContext}

${actionPlanContext}

HISTORIQUE CONVERSATION :
${conversationContext ? `Contexte des échanges précédents disponible (${Math.round(conversationContext.length/100)} caractères)` : 'Première conversation'}

INSTRUCTIONS SPÉCIALISÉES :
1- Réponds aux questions de l'utilisateur en tenant compte de son plan d'action
2- Utilise les sources disponibles pour enrichir tes réponses quand c'est pertinent
3- 🔗 IMPORTANT POUR LES LIENS : Quand tu références des sources web ou liens :
   • Utilise UNIQUEMENT le format markdown : [Texte du lien](https://url-complete.com)
   • ❌ N'utilise JAMAIS de HTML comme <a href="...">
   • ✅ Exemple correct : [Guide pratique](https://www.exemple.com)
   • Assure-toi que les URLs sont complètes avec https://
4- Recommande des sources spécifiques si elles peuvent aider l'utilisateur
5- IMPORTANT : Tiens compte du plan d'action pour contextualiser tes conseils :
   • Mentionne les étapes déjà terminées pour encourager l'utilisateur
   • Aide-le à planifier ou ajuster les prochaines étapes
   • Propose des actions concrètes liées aux étapes en cours
   • Identifie les blocages potentiels sur les étapes à venir
6- Pose 1 ou 2 questions pertinentes pour approfondir sa réflexion
7- Utilise le contexte pour personnaliser tes réponses
8- Garde un ton bienveillant, encourageant et orienté action
9- Limite tes réponses à 200 mots maximum
10- FORMATAGE : Utilise le markdown standard :
   * **texte** pour le gras
   * *texte* pour l'italique
   * ## Titre pour les titres
   * - Item pour les listes
   * [Lien](https://url.com) pour les liens
11- Utilise $...$ pour les formules mathématiques en ligne et $$...$$ pour les formules en mode affiché

APPROCHE COACHING AVEC PLAN D'ACTION :
- Célèbre les étapes accomplies
- Guide vers les prochaines actions prioritaires
- Aide à surmonter les obstacles identifiés
- Suggère des ajustements si nécessaire
- Maintiens la motivation et l'engagement

Réponds de manière claire, concise et adaptée au niveau et aux besoins de l'utilisateur, en gardant toujours son plan d'action comme fil conducteur.`;

    return basePrompt;
  }

  // 🔧 CORRECTION : Suppression du formatage HTML automatique
  formatMessageContent(content, role) {
    // On garde le contenu brut en markdown pour que EnhancedChatMarkdown le traite
    return content;
  }

  // Mise à jour intelligente du contexte de conversation
  updateConversationContext(chatMessages) {
    if (chatMessages.length === 0) return '';

    const recentMessages = chatMessages.slice(-6);
    const contextSummary = recentMessages
      .map(msg => `${msg.role}: ${msg.content.slice(0, 100)}`)
      .join('\n');
    
    return contextSummary.slice(0, CHAT_CONFIG.maxContextLength);
  }

  // Génération d'un nouveau message utilisateur
  createUserMessage(content) {
    this.lastMessageId++;
    return {
      id: Date.now() + this.lastMessageId,
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };
  }

  // Envoi d'un message à GPT avec gestion d'erreur et plan d'action
  async sendMessageToGPT({ userMessage, userObjective, searchObjective, savedSources, conversationContext, actionPlan }) {
    try {
      const systemPrompt = this.buildSystemPrompt({
        userObjective,
        searchObjective,
        savedSources,
        conversationContext,
        actionPlan
      });
      
      const fullMessage = `${systemPrompt}\n\nUtilisateur: ${userMessage}`;
      
      const response = await askGPT(fullMessage);
      
      if (!response) {
        throw new Error('Pas de réponse du service GPT');
      }
      
      // 🔧 CORRECTION : On garde la réponse brute sans formatage HTML
      return this.createAssistantMessage(response, {
        userObjective,
        searchObjective,
        sourcesCount: savedSources.length,
        actionPlanSteps: actionPlan?.steps?.length || 0,
        actionPlanProgress: actionPlan?.steps ? 
          Math.round((actionPlan.steps.filter(s => s.termine).length / actionPlan.steps.length) * 100) : 0
      });
    } catch (error) {
      console.error('❌ Erreur API GPT:', error);
      throw error;
    }
  }

  // Création d'un message assistant avec contexte du plan d'action
  createAssistantMessage(content, context = {}) {
    this.lastMessageId++;
    return {
      id: Date.now() + this.lastMessageId,
      role: 'assistant',
      // CORRECTION : S'assurer que content est toujours une chaîne
      content: String(content || ''),
      timestamp: new Date().toISOString(),
      context: {
        userObjective: context.userObjective,
        searchObjective: context.searchObjective,
        sourcesCount: context.sourcesCount || 0,
        messageNumber: context.messageNumber || 0,
        actionPlanSteps: context.actionPlanSteps || 0,
        actionPlanProgress: context.actionPlanProgress || 0
      }
    };
  }
  
  // Création d'un message d'erreur
  createErrorMessage(errorMessage = "Désolé, je rencontre une difficulté technique. Pouvez-vous répéter votre question ?") {
    this.lastMessageId++;
    return {
      id: Date.now() + this.lastMessageId,
      role: 'assistant',
      content: errorMessage,
      timestamp: new Date().toISOString(),
      isError: true
    };
  }

  // Gestion audio optimisée avec cache
  async generateAudio(messageContent) {
    if (!messageContent) {
      throw new Error('Pas de contenu à convertir en audio');
    }

    const contentHash = this.hashString(messageContent);
    if (this.audioCache.has(contentHash)) {
      console.log("🎵 Audio récupéré depuis le cache");
      return this.audioCache.get(contentHash);
    }

    try {
      const audioUrl = await getAudioFromGPT(messageContent);
      
      if (audioUrl) {
        if (this.audioCache.size >= 10) {
          const firstKey = this.audioCache.keys().next().value;
          const oldUrl = this.audioCache.get(firstKey);
          URL.revokeObjectURL(oldUrl);
          this.audioCache.delete(firstKey);
        }
        
        this.audioCache.set(contentHash, audioUrl);
        console.log("🎵 Audio généré et mis en cache");
        return audioUrl;
      } else {
        throw new Error('Impossible de récupérer l\'audio');
      }
    } catch (error) {
      console.error('❌ Erreur génération audio:', error);
      throw error;
    }
  }

  // Utilitaire pour créer un hash simple
  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }

  // Nettoyage des ressources audio
  cleanup() {
    this.audioCache.forEach(url => {
      URL.revokeObjectURL(url);
    });
    this.audioCache.clear();
    console.log("🧹 Cache audio nettoyé");
  }

  // Validation du message avant envoi
  validateMessage(inputText, userObjective) {
    if (!inputText.trim()) {
      throw new Error('Le message ne peut pas être vide');
    }

    if (!userObjective) {
      throw new Error('Veuillez définir un objectif pour utiliser le chat');
    }

    if (inputText.length > 1000) {
      throw new Error('Le message est trop long (maximum 1000 caractères)');
    }

    return true;
  }

  // Statistiques et métriques avec plan d'action corrigé
  getConversationStats(chatMessages, savedSources, conversationContext, currentActionPlan) {
    const baseStats = {
      messageCount: chatMessages.length,
      sourcesCount: savedSources.length,
      contextLength: conversationContext ? Math.round(conversationContext.length / 100) : 0,
      lastMessageTime: chatMessages.length > 0 ? chatMessages[chatMessages.length - 1].timestamp : null,
      userMessages: chatMessages.filter(msg => msg.role === 'user').length,
      assistantMessages: chatMessages.filter(msg => msg.role === 'assistant').length
    };

    if (currentActionPlan && currentActionPlan.steps) {
      const completedSteps = currentActionPlan.steps.filter(step => step.termine).length;
      const totalSteps = currentActionPlan.steps.length;
      
      baseStats.actionPlan = {
        totalSteps,
        completedSteps,
        pendingSteps: totalSteps - completedSteps,
        progressPercentage: Math.round((completedSteps / totalSteps) * 100),
        hasActivePlan: true
      };
    } else {
      baseStats.actionPlan = {
        totalSteps: 0,
        completedSteps: 0,
        pendingSteps: 0,
        progressPercentage: 0,
        hasActivePlan: false
      };
    }

    return baseStats;
  }

  // Analyser le plan d'action pour des suggestions
  analyzeActionPlanForSuggestions(actionPlan) {
    if (!actionPlan || !actionPlan.steps || actionPlan.steps.length === 0) {
      return {
        needsPlan: true,
        suggestions: ["Créer un plan d'action structuré pour atteindre votre objectif"]
      };
    }

    const suggestions = [];
    const completedSteps = actionPlan.steps.filter(step => step.termine);
    const pendingSteps = actionPlan.steps.filter(step => !step.termine);
    
    if (completedSteps.length === 0) {
      suggestions.push("Commencer par la première étape de votre plan d'action");
    } else if (pendingSteps.length === 0) {
      suggestions.push("Félicitations ! Votre plan est terminé. Pensez à définir de nouveaux objectifs");
    } else {
      suggestions.push(`Continuer avec la prochaine étape: "${pendingSteps[0].titre}"`);
    }

    const overdueTasks = pendingSteps.filter(step => {
      if (step.duree && step.duree.includes('semaine')) {
        return false;
      }
      return false;
    });

    if (overdueTasks.length > 0) {
      suggestions.push("Réviser les délais des étapes en cours");
    }

    return {
      needsPlan: false,
      progressPercentage: Math.round((completedSteps.length / actionPlan.steps.length) * 100),
      suggestions
    };
  }
}

// Instance singleton pour partager entre les composants
export const chatContextManager = new ChatContextManager();